import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ConfigResourceFieldType, ConfigResourceTypes } from '../models/config.resources.model';
import { NgbDateAdapter, NgbDateParserFormatter, NgbDatepickerI18n, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { DateTime } from 'luxon';
import { toDateTime } from '../utilities/date-helpers';
import { TimetableData, TimetableDataList } from '../models/timetable.model';
import { TicketsData } from '../models/tickets.model';
import { Observable, of, Subject } from 'rxjs';

import * as Mustache from 'mustache';
import currencyToSymbolMap from 'currency-symbol-map/map'

import { EnvironmentService } from './environment.service';
import { catchError, map } from 'rxjs/operators';
import { SurveyData } from '../models/survey.model';
import { PersistenceService } from './persistence.service';
import { PGLocation } from '../models/map.model';
import { PGUtilities } from '../utilities/pg-utilities';
import { OptionMapsService } from './option-maps.service';

export class LocalizationData {

    weekStart:number;
    dayNames:Array<string>;
    monthNames:Array<string>;

    dateFormats: {
        short:string,
        long:string,
        shortMonthDay:string,
        longMonthDay:string
    };

    timeFormats: {
        toMinutes:string,
        toSeconds:string
    };

    shortDays:string;

    decimalSeparator:string;
    thousandsSeparator:string;

    translations: {
        [id:string]: any
    }
}

export class LocalizationFormat {
    constructor(private service:LocalizationService, private environmentService:EnvironmentService) {}

    boolean(booleanVal:any):string {
        if(booleanVal) {
            return '<i class="fa-regular fa-check"></i>'
        }
        else {
            return '<i class="fa-regular fa-times"></i>'
        }
    }

    date(dateVal:number|string|Date|{ year: number, month: number, day: number }, longFormat?:boolean, noYear?:boolean):string {
        if(dateVal != null) {
            let _formatName = longFormat ? 'long' : 'short';
            
            if(typeof dateVal == 'object' && !(dateVal instanceof Date)) {
                if(dateVal.year == null) {
                    dateVal.year = 1970
                    noYear = true;
                }

                dateVal = new Date(dateVal.year, dateVal.month - 1, dateVal.day)
            }

            if(noYear) _formatName += 'MonthDay'

            let _format = this.service.data.dateFormats[_formatName];

            let _dt = toDateTime(dateVal);

            // Luxon weekday è 1-7 (Monday=1), moment era 0-6 (Sunday=0)
            // Luxon month è 1-12, moment era 0-11
            _format = _format.replace(/DayN/g, '[' + this.service.data.dayNames[_dt.weekday - 1] + ']')
            _format = _format.replace(/MonthN/g, '[' + this.service.data.monthNames[_dt.month - 1] + ']')

            // Converti formato moment → luxon
            _format = _format.replace(/YYYY/g, 'yyyy').replace(/DD/g, 'dd').replace(/MM/g, 'LL');

            return _dt.setLocale(this.service.currentLanguage.split('_')[0]).toFormat(_format);
        }
    }

    duration(timeVal:number|string, sec?:boolean, ms?:boolean, noDays?:boolean) {
        if(timeVal != null) {
            if(typeof timeVal == 'string') {
                let _timeSplit = timeVal.split(':');

                timeVal = 0;

                for(let i = 0; i < 3; i++) {
                    if(_timeSplit[i] != null) {
                        let _cVal = parseInt(_timeSplit[i]);

                        for(let j = i; j < 2; j++) {
                            _cVal *= 60
                        }

                        timeVal += _cVal * 1000;
                    }
                }
            }
            
            let _days = Math.floor(timeVal / 1000 / 60 / 60 / 24)
            let _hours = Math.floor((timeVal / 1000 / 60 / 60) % 24)
            let _minutes = Math.floor((timeVal / 1000 / 60) % 60)

            if(noDays) {
                _hours += _days * 24;
                _days = 0;
            }

            let _retVal = ''

            if(_days > 0) {
                _retVal += _days + this.service.data.shortDays;
            }

            if(_hours != 0 || _minutes != 0 || sec || ms) {
                if(_retVal != '') _retVal += ' ';

                let _hoursString = _hours.toString();
                if(_hoursString.length < 2) _hoursString = '0' + _hoursString;

                _retVal += _hoursString;

                let _minutesString = _minutes.toString();
                if(_minutesString.length < 2) _minutesString = '0' + _minutesString;
    
                _retVal += ':' + _minutesString;
            }

            if(sec) {
                if(_retVal != '') _retVal += ':';

                let _seconds = Math.floor((timeVal / 1000) % 60)

                let _secondsString = _seconds.toString();
                if(_secondsString.length < 2) _secondsString = '0' + _secondsString;
    
                _retVal += _secondsString;

                if(ms) {
                    let _ms = Math.floor(timeVal) % 1000;

                    let _msString = _ms.toString();
                    if(_msString.length < 3) _msString = '0' + _msString;
                    if(_msString.length < 3) _msString = '0' + _msString;
        
                    _retVal += '.' + _msString;
                }
            }

            return _retVal;
        }
    }

    time(timeVal:number|string|Date, sec?:boolean):string {
        if(timeVal != null) {
            let _dt = toDateTime(timeVal);
            let _format = this.service.data.timeFormats.toMinutes
            if(sec) _format = this.service.data.timeFormats.toSeconds

            if(_dt.isValid) {
                return _dt.toFormat(_format);
            }
            else if(typeof timeVal == 'string') {
                if(/\d\d:\d\d:\d\d/.test(timeVal)) return DateTime.fromFormat(timeVal, 'HH:mm:ss').toFormat(_format);
                else if(/\d\d:\d\d/.test(timeVal)) return DateTime.fromFormat(timeVal, 'HH:mm').toFormat(_format);
            }
        }
    }

    datetime(dateTimeVal:any, sec?:boolean):string {
        if(dateTimeVal != null) {
            dateTimeVal = toDateTime(dateTimeVal).toLocal().toFormat('yyyy-LL-dd HH:mm:ss');
            return this.date(dateTimeVal) + ' ' + this.time(dateTimeVal, sec);
        }
    }

    distance(val:number) {
        if(val != null) {
            if(val > 1000) {
                return (Math.round(val / 100) / 10).toString() + ' Km'
            }
            else return val + ' m';
        }
    }

    decimal(decimalVal:number):string {
        if(decimalVal == null) return '';
        else {
            if(typeof decimalVal == 'string') {
                decimalVal = parseFloat(decimalVal);
            }

            if(isNaN(decimalVal)) return '';
            else return decimalVal.toFixed(2).replace('.', this.service.data.decimalSeparator);
        }
    }

    price(priceVal:number, currency?:string):string {
        if(!currency) currency = this.environmentService.getDefaultCurrency();
        return this.decimal(priceVal) + ' ' + this.service.currencySymbols[currency]
    }

    percentage(percVal:number):string {
        return this.decimal(percVal) + '%';
    }

    password():string {
        return '***';
    }

    html(htmlVal:string):string {
        if(htmlVal != null) {
            return '<div class="PGFormatHTML">' + htmlVal + '</div>';
        }
    }

    constraint(operator:string, field:string) {
        return this.service.translate('constraint.' + operator, { field: field });
    }

    rating(value:number) {
        let _retVal = '';

        for(let i = 0; i < 5; i++) {
            if(i < value) {
                _retVal += '<i class="fa-solid fa-star text-primary"></i>'
            }
            else {
                _retVal += '<i class="fa-regular fa-star"></i>'
            }
        }

        return _retVal;
    }

    location(value:PGLocation, size?:{ width?:number, height?:number, zoom?:number }) {
        if(size == null) size = {}
        if(size.width == null) size.width = 400;
        if(size.height == null) size.height = 200;
        if(size.zoom == null) size.zoom = 13;

        if(value == null) return '';
        else {
            if(value.type == 'Point') {
                return '<img src="https://maps.googleapis.com/maps/api/staticmap?zoom=' + size.zoom + '&size=' + size.width + 'x' + size.height + '&' +
                'markers=color:red|label:|' + value.coordinates[0] + ',' + value.coordinates[1] + '&key=' + this.environmentService.environment.GoogleMapsAPIKey + '"/>' 
            }
        }
    }

    timetable(type:'slots'|'openings'|'periods'|'dates'|'departures'|'event', cVal:any, skipSlotDuration?:boolean) {
        if(cVal == null) return '';
        else {
            let _retVal = '';

            let _cObj:TimetableData|Array<TimetableData>|TimetableDataList = cVal;

            if(typeof cVal == 'string') {
                try {
                    _cObj = JSON.parse(cVal);
                }
                catch(ex) {
                    _cObj = null;
                }
            }

            if(_cObj != null) {
                if('toData' in _cObj) {
                    _cObj = _cObj.toData()
                }

                if('length' in _cObj) {
                    if(_cObj.length > 0) {
                        _retVal += '<table class="PGFormatTable PGFormatTable--Timetable">'
                        
                        let _isFirst = true;
        
                        if(type == 'slots' && _cObj[0] != null && _cObj[0].slotDuration != null) {
                            _retVal += '<tr class="PGFormatTable--Timetable-SlotDuration"><td><b>' + this.service.translate('pg-timetable-editor.experience-duration') + '</b> ' + _cObj[0].slotDuration.replace(/^(\d\d:\d\d):\d\d$/, '$1') + '</td></tr>';
                            _isFirst = false;
                        }
        
                        for(let _cItem of _cObj) {
                            if(_isFirst) {
                                _isFirst = false;
                                _retVal += '<tr>';
                            }
                            else {
                                _retVal += '<tr class="pt-2">';
                            }
        
                            _retVal += '<td>' + this.timetable(type, _cItem, true) + '</td>';
        
                            _retVal += '</tr>';
                        }
        
                        _retVal += '</table>'
                    }
                }
                else {
                    if(!skipSlotDuration && _cObj.slotDuration != null) {
                        if(type == 'slots') {
                            _retVal += '<div class="PGFormatTable--Timetable-SlotDuration"><b>' + this.service.translate('pg-timetable-editor.experience-duration') + '</b> ' + _cObj.slotDuration.replace(/^(\d\d:\d\d):\d\d$/, '$1') + '</div>';
                        }
                        else if(type == 'departures') {
                            let _val:number = null;
    
                            let _timeSplit = _cObj.slotDuration.split(':');
                            _val = parseInt(_timeSplit[0]) * 60 + parseInt(_timeSplit[1])
                            _val /= 1440
    
                            _retVal += '<div class="PGFormatTable--Timetable-SlotDuration"><b>' + this.service.translate('pg-timetable-editor.package-duration') + '</b> ' + _val + '</div>';
                        }
                    }
    
                    if(type != 'periods' && type != 'dates' && type != 'event') {
                        _retVal += '<div><b>' + this.service.translate('pg-timetable-editor.activity-periods') + '</b></div>';
                    }
                    
                    if(_cObj.periods.length == 0) {
                        _retVal += '<div>' + this.service.translate('pg-timetable-editor.all-year') + '</div>';
                    }
                    else {
                        let _cDaysString = '';
        
                        for(let _cDay of _cObj.periods) {
                            if(_cDay.begin.month == 1 && _cDay.begin.day == 1 && _cDay.end.month == 12 && _cDay.end.day == 31) {
                                _retVal += '<div>' + this.service.translate('pg-timetable-editor.all-year') + '</div>';
                            }
                            else {
                                let _beginYear = _cDay.year;
                                let _endYear = _cDay.year;

                                if(_beginYear != null) {
                                    if(_cDay.end.month < _cDay.begin.month || (_cDay.end.month == _cDay.begin.month && _cDay.end.day < _cDay.begin.day)) {
                                        _endYear++;
                                    }
                                }

                                _cDaysString += '<div>'

                                if(_beginYear == _endYear && _cDay.begin.month == _cDay.end.month && _cDay.begin.day == _cDay.end.day) {
                                    _cDaysString += ' ' + this.service.translate('pg-timetable-editor.on-day') + ' ' + this.date({ year: _beginYear, month: _cDay.begin.month, day: _cDay.begin.day });
                                }
                                else {
                                    _cDaysString += this.service.translate('pg-timetable-editor.from-day') + ' ' + this.date({ year: _beginYear, month: _cDay.begin.month, day: _cDay.begin.day });
                                    _cDaysString += ' ' + this.service.translate('pg-timetable-editor.to-day') + ' ' + this.date({ year: _endYear, month: _cDay.end.month, day: _cDay.end.day });
                                }

                                if(type == 'event' && _cObj.hours != null && _cObj.hours[0] != null) {
                                    _cDaysString += ' ' + this.service.translate('pg-timetable-editor.from-hours') + ' ' + this.time(_cObj.hours[0].begin)
                                }

                                _cDaysString += '</div>'
                            }
                        }
        
                        _retVal += _cDaysString
                    }

                    if(_cObj.unavailable) {
                        _retVal += '<div class="text-danger">'
                        this.service.translate('pg-timetable-editor.unavailable-period')
                        _retVal += '</div>'
                    }
                    else {
                        if(type != 'periods' && type != 'dates' && type != 'event') {
                            _retVal += '<div><b>' + this.service.translate('pg-timetable-editor.times-day') + '</b></div>';
            
                            let _prevDayString = null;
                
                            if(_cObj.hours != null) {
                                for(let _cHour of _cObj.hours) {
                                    let _cDaysString = '<div class="d-flex my-1">';
    
                                    if(_cHour.days != null) {
                                        for(let i = 0; i < 7; i++) {
                                            let _index = (this.service.data.weekStart + i) % 7;
        
                                            _cDaysString += '<div class="PGTimetableFormat-Day ' + (_cHour.days[_index] ? 'active' : '') + '">' + this.service.data.dayNames[_index].charAt(0) + '</div>'
                                        }
                                    }
                                    
                                    _cDaysString += '</div>'
                
                                    if(_cDaysString != _prevDayString) {
                                        _retVal += _cDaysString;
                                        _prevDayString = _cDaysString;
                                    }
            
                                    if(_cHour.begin != null && _cHour.end != null) {
                                        let _cHoursString = '<span class="badge bg-primary me-2">' + _cHour.begin;
                                        if(type != 'departures') _cHoursString += ' - ' + _cHour.end;
                                        _cHoursString += '</span>';
    
                                        let _info = _cHour.info
    
                                        if(_info) {
                                            _cHoursString += '<small><i class="fa-regular fa-circle-info text-muted"></i>';
                                            if(typeof _info == 'string') {
                                                _info = PGUtilities.tryParseJSON(_info)
                                            }
    
                                            if(typeof _info == 'string') {
                                                _cHoursString += _info
                                            }
                                            else {
                                                for(let _language in _info) {
                                                    _cHoursString += '<span class="badge bg-primary PGLanguageBadge ms-2 me-1">' + _language.split('_')[0].toUpperCase() + '</span>' + _info[_language]
                                                }
                                            }
    
                                            _cHoursString += '</small>';
                                     
                                            
                                        }
                        
                                        _retVal += '<div class="PGTimetableFormat-Hours">' + _cHoursString + '</div>';
                                    }
                                }
                            }
                        }
                    }
                }
            }

            return _retVal;
        }
    }

    tickets(type:'experience'|'host', cVal:any, currency?:string) {
        if(cVal == null) return '';
        else {
            if(!currency) currency = this.environmentService.getDefaultCurrency();
            let _currencySymbol = this.service.currencySymbols[currency]

            let _retVal = '';

            let _cObj:TicketsData = JSON.parse(cVal);

            
            if(_cObj.fullPrice == null) {
                _retVal += '<tr><td>' + this.service.translate('pg-tickets-editor.' + type + '-price-not-specified') + '</td></tr>';
            }
            else {
                _retVal += '<tr><td>' + this.service.translate('pg-tickets-editor.full-price' + (type == 'host' ? '-single' : '')) + '</td><td class="ps-3 text-end">' + _cObj.fullPrice + ' ' + _currencySymbol + '</td></tr>';

                if(_cObj.otherTickets != null) {         
                    for(let _cOther of _cObj.otherTickets) {
                        _retVal += '<tr class="border-top">';
        
                        let _namesString = '';
        
                        if(typeof _cOther.name == 'string') {
                            _namesString = _cOther.name;
                            if(_namesString == null) _namesString = '';
                        }
                        else {
                            for(let _cLanguage in _cOther.name) {
                                let _cName = _cOther.name[_cLanguage];
                                if(_cName == null) _cName = '';
                                
                                if(_namesString != '') _namesString += '<br/>';
                                _namesString += '<span class="badge bg-primary PGLanguageBadge">' + _cLanguage.split('_')[0].toUpperCase() + '</span> ' + _cName;
                            }
                        }
        
                        _retVal += '<td>' + _namesString + '</td><td class="ps-3 text-end">' + _cOther.price + ' ' + _currencySymbol + '</td>'
                        _retVal += '</tr>';
                    }
                }
            }

            if(_retVal != '') {
                _retVal = '<table class="PGFormatTable">' + _retVal + '</table>';
            }

            return _retVal
        }
    }

    survey(cVal:any) {
        if(cVal == null) return '';
        else {
            let _retVal = '';

            let _cObj:SurveyData = cVal;
            
            if(typeof cVal == 'string') _cObj = PGUtilities.tryParseJSON(cVal);

            if(_cObj != null &&  _cObj.questions != null) {
                let _isFirst = true;

                for(let _cQuestion of _cObj.questions) {
                    if(_isFirst) {
                        _isFirst = false;
                        _retVal += '<tr>';
                    }
                    else {
                        _retVal += '<tr class="border-top">';
                    }

                    let _namesString = '';

                    if(typeof _cQuestion.text == 'string') {
                        _namesString = _cQuestion.text;
                        if(_namesString == null) _namesString = '';
                    }
                    else {
                        for(let _cLanguage in _cQuestion.text) {
                            let _cName = _cQuestion.text[_cLanguage];
                            if(_cName == null) _cName = '';
                            
                            if(_namesString != '') _namesString += '<br/>';
                            _namesString += '<span class="badge bg-primary PGLanguageBadge">' + _cLanguage.split('_')[0].toUpperCase() + '</span> ' + _cName;
                        }
                    }

                    _retVal += '<td>' + _namesString + '</td>' +
                        '<td class="px-2 text-center">' + this.service.translate('pg-survey-editor.answer-type-' + _cQuestion.type) + '</td>' +
                        '<td class="ps-2 text-center">' + (_cQuestion.required ? '<i class="fa-regular fa-check"></i>' : '') + '</td>';

                    _retVal += '</tr>';
                }
            }

            if(_retVal != '') {
                _retVal = '<table class="PGFormatTable"><thead><tr>' +
                    '<th><b>' + this.service.translate('pg-survey-editor.question-text') + '</b></th>' +
                    '<th class="px-2 text-center"><b>' + this.service.translate('pg-survey-editor.answer-type') + '</b></th>' +
                    '<th class="ps-2 text-center"><b>' + this.service.translate('pg-survey-editor.answer-required') + '</b></th>' +
                    '</tr></thead><tbody>' + _retVal + '</tbody></table>'
            }

            return _retVal
        }
    }

    color(val:any) {
        if(val != null && val != '') {
            return '<span class="PGColorDisplay" style="background-color: ' + val + '"></span> ' + val
        }
    }

    social(val:any) {
        let _retVal = ''

        if(typeof val == 'string') {
            val = PGUtilities.tryParseJSON(val)
        }
        
        if(val != null) {
            for(let _social of ['facebook','instagram','youtube','tiktok']) {
                if(val[_social]) {
                    _retVal += '<tr><td><i class="fa-brands fa-fw fa-' + _social + ' me-1"></i></td>' +
                        '<td><a target="_blank" href="';

                    if(_social == 'facebook') {
                        _retVal += 'https://www.facebook.com/' + val[_social]
                    }
                    else if(_social == 'instagram') {
                        _retVal += 'https://www.instagram.com/' + val[_social]
                    }
                    else if(_social == 'youtube') {
                        _retVal += 'https://www.youtube.com/@' + val[_social].replace(/^@/, '')
                    }
                    else if(_social == 'tiktok') {
                        _retVal += 'https://www.tiktok.com/@' + val[_social].replace(/^@/, '')
                    }

                    _retVal += '">' + val[_social] + '</a></td>'
                }
            }

            if(_retVal != '') {
                _retVal = '<table class="PGFormatTable PGFormatTable--Social"><tbody>' + _retVal + '</tbody></table>'
            }

            return _retVal;
        }
    }

    byType(val:any, type?:string, extraParam?:any):string {
        let _subtype = null;
        
        if(type != null) {
            _subtype = type.split('-')[1]
            type = type.split('-')[0]
        }
        

        if(val == null) return '';
        else {
            if(type == null) {
                type = 'string';
            }

            if(this[type] != null) {
                if(_subtype) return this[type](_subtype, val, extraParam);
                else return this[type](val, extraParam);
            }
            else {
                return val.toString();
            }
        }
    }

    private _internalLanguageHandlingTypes:Array<ConfigResourceFieldType> = ['tickets-experience','tickets-host','survey','timetable-slots'];

    hasTypeInternalLanguageHandling(type:ConfigResourceFieldType) {
        return this._internalLanguageHandlingTypes.indexOf(type) != -1;
    }

    byConfig(cVal:any, cConfig?:ConfigResourceTypes, extraParam?:any):string {
        if(cConfig == null) {
            return this.byType(cVal);
        }
        else if (cConfig.locale && !this.hasTypeInternalLanguageHandling(cConfig.type)) {
            let _retVal = ''
            
            if(cVal != null) {
                let _cValObj = JSON.parse(cVal)

                if(_cValObj != null) {
                    for(let i in _cValObj) {
                        if(_cValObj[i] != null) {
                            _retVal += '<span class="badge bg-primary PGLanguageBadge">' + i.split('_')[0].toUpperCase() + '</span> ' + this.byType(_cValObj[i], cConfig.type, extraParam) + '<br/>'
                        }
                    }
                }
            }

            return _retVal;
        }
        else if(cConfig.type == 'file') {
            let _retVal = '';

            if(cVal) {
                let _checkVal = cVal;

                if(cConfig.multi) {
                    _checkVal = JSON.parse(cVal);
                }
                else {
                    _checkVal = [_checkVal];
                }

                for(let _val of _checkVal) {
                    if(_retVal != null) _retVal += ' '

                    switch(cConfig.fileType) {
                        case 'images': _retVal += '<div><img src="' + _val + '"/></div>'; break;
                        case 'videos': _retVal += '<div><video><source src="' + _val + '" type="video/mp4"/></video></div>'; break;
                        default: _retVal += '<a target="_blank" href="' + _val + '">' + _val.replace(/^.*\//, '') + '</a>'; break; 
                    }
                }

                if(_retVal != '') {
                    _retVal = '<div class="PGFileFormat">' + _retVal + '</div>';
                }
            }

            return _retVal;
        }
        else if(cConfig.type == 'select')  {
            let _retVal = '';

            if(cVal != null) {
                let _selValues = [cVal];

                if(cConfig.useKeys) {
                    _selValues = []

                    let _parsed = PGUtilities.tryParseJSON(cVal);
                    if(_parsed) {
                        for(let i in _parsed) {
                            if(_parsed[i]) _selValues.push(i)
                        }
                    }

                }
                else if(cConfig.multi) {
                    _selValues = PGUtilities.tryParseJSON(cVal) || [];
                }

                for(let i = 0; i < _selValues.length; i++) {
                    let _selVal = _selValues[i];

                    if(_retVal != '') _retVal += ', ';

                    var _isIn = false;

                    if(cConfig.options != null) { // TODO: se c'è resource sembra che non carichi le options e quindi non formatti una mazza
                        for(let j = 0; j < cConfig.options.length; j++) {
                            if(cConfig.options[j].value == _selVal) {
                                _isIn = true;
                                _retVal += cConfig.options[j].text;
                                break;
                            }
                        }
                    } 

                    if(!_isIn) _retVal += _selVal;
                }
            }

            return _retVal;
        }
        else if(cConfig.type == 'embed') {
            let _parsed = PGUtilities.tryParseJSON(cVal)

            if(_parsed != null) {
                if(!cConfig.multi) {
                    _parsed = [_parsed]
                }
    
                if('length' in _parsed) {
                    for(let _item of _parsed) {
                        return '<iframe src="' + PGUtilities.cleanEmbedSrc(_item.src) + '"></iframe>';                                
                    }
                }
            }
        }
        else {
            return this.byType(cVal, cConfig.type, extraParam);
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class LocalizationService {

    public currentLanguageChange = new Subject<string>();

    currentLanguage:string = null;

    public availableLanguagesChange = new Subject<Array<string>>();

    languageLabels = { 
        'en_EN': 'English',
        'it_IT': 'Italiano',
        'de_DE': 'Deutsch',
        'es_ES': 'Español',
        'fr_FR': 'Français',
        'pt_PT': 'Português',
        'ru_RU': 'Русский',
        'zh_ZH': '國語',
        'hr_HR': 'Hrvatski',
        'sr_SR': 'Српски',
        'bs_BA': 'Bosanski',
        'sl_SL': 'Slovenščina',
        'el_EL': 'Ελληνικά',
        'sq_AL': 'Shqip',
    }

    currencySymbols:{ [id:string]: string } = null;

    availableApplicationLanguages:Array<string> = [ 'en_EN', 'it_IT' ];
    defaultContentLanguages:Array<string> = [ 'en_EN' ]

    data:LocalizationData = null;
    fallbackData:LocalizationData = null;

    format:LocalizationFormat = null;

    icons:any = null;

    constructor(private http:HttpClient, private environmentService:EnvironmentService, private persistenceService:PersistenceService) {
        this.format = new LocalizationFormat(this, environmentService);
    }

    async init() {
        if(this.environmentService.environment.DefaultContentLanguages?.length) this.defaultContentLanguages = this.environmentService.environment.DefaultContentLanguages;
        if(this.environmentService.environment.AvailableApplicationLanguages?.length) this.availableApplicationLanguages = this.environmentService.environment.AvailableApplicationLanguages;

        this.currencySymbols = currencyToSymbolMap;

        this.icons = await this._getJSONAndCatchError('assets/icons/fontawesome.json');

        this.fallbackData = await this.getLanguageData(this.availableApplicationLanguages[0]);

        this.disableTranslations = this.persistenceService.getItem('POIGEST_DisableTranslations') == 'true';

        // TOFIX: qua setta sempre la lingua anche se è la stessa salvata nei dati utente, ha senso?
        let _setLanguage = this.persistenceService.getItem('POIGEST_Language');

        if(this.environmentService.externalParameters.language != null) _setLanguage = this.environmentService.externalParameters.language;

        if(_setLanguage != null) {
            await this.setLanguage(_setLanguage)
        }
        else {
            await this.setLanguage(navigator.language)
        }
    }

    private _languageDataFromHTML(source:string) {
        let _retVal:any = {}

        if(source != null) {
            let _content = document.createElement('div')
            _content.innerHTML = source;
    
            let _elementList = _content.querySelectorAll('[data-tid]')
    
            _elementList.forEach((element) => {
                let _tid = element.getAttribute('data-tid');
                let _value = element.innerHTML;
    
                this._addTranslationByTid(_tid, _value, _retVal)
            })
        }

        return { translations: _retVal };
    }

    private _languageDataMerge(source:any, destination:any) {
        if(source != null) {
            for(let i in source) {
                if(source[i] != null) {
                    if(destination[i] == null || typeof destination[i] != 'object') destination[i] = JSON.parse(JSON.stringify(source[i]))
                    else {
                        if(typeof source[i] == 'object') {
                            this._languageDataMerge(source[i], destination[i])
                        }
                        else destination[i] = source[i];
                    }
                }
            }
        }
    }

    private _getJSONAndCatchError(url:string, text?:boolean):Promise<any> {
        return new Promise((resolve, reject) => {
            this.http.get(url, {
                headers: {
                    'X-Interceptor-Silent': '*',
                }
            }).pipe((catchError(() => {
                return of(null)
            }))).subscribe((data) => {
                resolve(data)
            });
        })  
    }

    private _getHTMLAndCatchError(url:string):Promise<any> {
        return new Promise((resolve, reject) => {
            this.http.get(url, {
                headers: {
                    'X-Interceptor-Silent': '*',
                },
                responseType: 'text'
            }).pipe((catchError(() => {
                return of(null)
            }))).subscribe((data) => {
                resolve(this._languageDataFromHTML(data))
            });
        })  
    }

    private _cachedLanguageData:{ [langId:string]: any } = {}

    async getLanguageData(langId:string):Promise<any> {
        if(this._cachedLanguageData[langId] != null) {
            return JSON.parse(JSON.stringify(this._cachedLanguageData[langId]))
        }
        else {
            let _retVal:any = {};
        
            this._languageDataMerge(await this._getJSONAndCatchError('assets/localization/' + langId + '.json'), _retVal);
            this._languageDataMerge(await this._getHTMLAndCatchError('assets/localization/' + langId + '.html'), _retVal);
            
            this._languageDataMerge(await this._getJSONAndCatchError('assets/localization-app/' + langId + '.json'), _retVal);
            this._languageDataMerge(await this._getHTMLAndCatchError('assets/localization-app/' + langId + '.html'), _retVal);
    
            await this._checkLoadContentData()
    
            if(this.contentDataByTid != null) {
                let _translationsObject:any = {};
    
                for(let i in this.contentDataByTid) {
                    this._applyContentData(i, langId, _translationsObject)
                }
    
                this._languageDataMerge({ translations: _translationsObject }, _retVal);
            }
    
            this._cachedLanguageData[langId] = _retVal;
    
            return _retVal
        }
    }

    contentDataByTid: { [tid:string]: any } = null;

    setContentData(item:any) {
        if(item.tid != null) {
            this.contentDataByTid[item.tid] = item;
            this._applyContentData(item.tid, this.currentLanguage, this.data.translations)
        }
    }

    private _applyContentData(tid:string, langId:string, destination:any) {
        let _cElement = this.contentDataByTid[tid];
                                
        if(_cElement[langId] != null) {
            this._addTranslationByTid(tid, _cElement[langId], destination)
        }   
    }

    private _addTranslationByTid(tid:string, value:string, destination:string) {
        let _nameSplit = tid.split('.');

        let _cObj = destination;

        for(let i = 0; i < _nameSplit.length - 1; i++) {
            if(_cObj[_nameSplit[i]] == null) _cObj[_nameSplit[i]] = {}
            _cObj = _cObj[_nameSplit[i]]
        }

        _cObj[_nameSplit[_nameSplit.length - 1]] = value;
    }

    private _checkLoadContentData() {
        return new Promise<void>((resolve) => {
            if(this.contentDataByTid != null) resolve();
            else {
                this.http.get(this.environmentService.environment.APIUrl + '/public/content?limit=5000')
                .pipe((catchError(() => {
                    return of([])
                }))).subscribe((data:Array<any>) => {
                    this.contentDataByTid = {}

                    for(let _item of data) {
                        if(_item.tid != null) {
                            this.contentDataByTid[_item.tid] = _item;
                        }
                    }
                    
                    resolve();
                })
            }
        })
    }

    additionalLanguages:Array<string> = null;

    setAdditionalLanguages(list:Array<string>) {
        this.additionalLanguages = list;
        
        this.availableApplicationLanguages = []

        if(this.environmentService.environment?.AvailableApplicationLanguages) {
            for(let _language of this.environmentService.environment?.AvailableApplicationLanguages) {
                this.availableApplicationLanguages.push(_language)
            }
        }

        if(this.additionalLanguages) {
            for(let _language of this.additionalLanguages) {
                this.availableApplicationLanguages.push(_language)
            }
        }

        this.availableLanguagesChange.next(this.availableApplicationLanguages)
    }

    normalizeLanguage(langId:string) {
        if(langId != null) { // normalizzo langId in modo che accetti anche stringhe da 2 lettere o separate da -
            langId = langId.replace('-', '_')
            if(!/_/.test(langId)) langId = langId.substring(0, 2).toLowerCase() + '_' + langId.substring(0, 2).toUpperCase()
        }

        return langId;
    }

    async setLanguage(langId:string):Promise<any> {
        langId = this.normalizeLanguage(langId);

        if(this.availableApplicationLanguages.indexOf(langId) == -1) { // se la lingua specifica non c'è prendo la prima che ha la prima parte uguale, in modo che per esempio en_GB selezioni en_EN
            for(let _language of this.availableApplicationLanguages) {
                if(langId.substring(0, 2) == _language.substring(0, 2)) {
                    langId = _language;
                    break;
                }
            }
        }

        if(this.availableApplicationLanguages.indexOf(langId) == -1) {
            return await this.setLanguage(this.availableApplicationLanguages[0]);
        }
        else {
            let _data = await this.getLanguageData(langId)

            this.data = JSON.parse(JSON.stringify(this.fallbackData))
   
            this._languageDataMerge(_data, this.data);

            this.currentLanguage = langId;

            this.persistenceService.setItem('POIGEST_Language', this.currentLanguage, 'technical');

            document.documentElement.setAttribute('lang', this.currentLanguage.split('_')[0])

            this.currentLanguageChange.next(this.currentLanguage)

            return this.currentLanguage;
        }
    }

    private _addedTranslationAliases:{ [id:string] : string } = {}

    addTranslationAlias(id:string, aliasOf:string) {
        this._addedTranslationAliases[id] = aliasOf;
    }

    private _addedTranslationLinks:{ [id:string] : string } = {}

    addTranslationLink(id:string, aliasOf:string) {
        this._addedTranslationLinks[id] = aliasOf;
    }

    /** Shared event type labels (notifications + analytics). */
    getNotificationEventLabel(eventType: string): string {
        return this.translate('NOTIFICATION_EVENTS.' + eventType);
    }

    private _addedTranslations:{ [language:string] : { [id:string]: string } } = {
        '*': {}  // NB: language "*" = fallback 
    };

    addTranslation(language:string, id:string, translation:string) {
        if(this._addedTranslations[language] == null) this._addedTranslations[language] = {};
        this._addedTranslations[language][id] = translation;
    }

    private _getTranslationOrObject(id:string):string|{ [id:string]:string } {
        if(this.data == null) return null;
        else {
            if(this._addedTranslationAliases[id] != null) id = this._addedTranslationAliases[id];
            else {
                for(let i in this._addedTranslationLinks) {
                    if(id.startsWith(i)) {
                        id = id.replace(i, this._addedTranslationLinks[i])
                    }
                }
            }
    
            let _retVal = null;
    
            if(this._addedTranslations[this.currentLanguage] != null && this._addedTranslations[this.currentLanguage][id] != null) {
                _retVal = this._addedTranslations[this.currentLanguage][id]
            }
            else {
                let _idSplit = id.split('.');
                _retVal = this.data.translations;
        
                for(let i = 0; i < _idSplit.length; i++) {
                    if(_retVal != null) _retVal = _retVal[_idSplit[i]];
                }
            }

            let _resourcesRegExp = /^RESOURCES\.[^\*&^\.]*\./;
            
            if(_retVal == null && _resourcesRegExp.test(id)) {
                _retVal = this._getTranslationOrObject(id.replace(_resourcesRegExp, 'RESOURCES.*.'));
            }
    
            if(_retVal == null && this._addedTranslations['*'][id] != null) {
                _retVal = this._addedTranslations['*'][id]
            }
    
            return _retVal;
        }
    }

    getTranslationObject(id:string):{ [id:string]:string } {
        let _obj = this._getTranslationOrObject(id);
        if(typeof _obj != 'string') return _obj;
    }

    translate(id:string, replaceData?:any, forceReplace?:boolean, nullIfNotFound?:boolean):string {
        if(id != null) {
            if(this.disableTranslations) return id
            else if(/^\(/.test(id)) {
                let _cFn = eval(id);
                return _cFn(this, replaceData)
            }
            else {
                let _translation = this._getTranslationOrObject(id);

                if(typeof _translation != 'string' && forceReplace) _translation = id;

                if(typeof _translation == 'string') {
                    if(replaceData != null) {
                        let _replaceData:any = {}

                        for(let i in replaceData) {
                            _replaceData[i] = replaceData[i];

                            if(typeof _replaceData[i] == 'string') {
                                let _splitList = _replaceData[i].split('|')
    
                                if(_splitList.length > 1) {
                                    let _val = _splitList[0];
    
                                    for(let j = 1; j < _splitList.length; j++) {
                                        if(this.format[_splitList[j]] != null) {
                                            _val = this.format[_splitList[j]](_val)
                                        }
                                    }
    
                                    _replaceData[i] = _val;
                                }
                            }
                        }

                        _translation = Mustache.render(_translation, _replaceData);
                    }

                    return _translation
                }
                else if(nullIfNotFound) {
                    return null;
                }
                else {
                    return id + (replaceData ? ' ' + JSON.stringify(replaceData) : '');
                }
            }
        }
    }

    getCleanResourceId(resource:string) {
        if(resource != null) {
            return resource.replace(/~.*$/, '');
        }
    }

    translateResource(resource:string) {
        if(resource != null) {
            return this.translate('RESOURCES.' + resource + '.name')
        }
    }

    translateResourceField(resource:string, field:string) {
        if(resource != null && field != null) {
            return this.translate('RESOURCES.' + this.getCleanResourceId(resource) + '.fields.' + field + '.label')
        }
    }

    translateOptionMapValue(resource:string, field:string, value:string) {
        return this.translate('OPTIONMAPS.' + resource + '.' + field + '.' + value, null, null, true) || value
    }

    private _remoteTranslateCache:{
        [from:string]: {
            [to:string]: Array<{
                original:string,
                translated:string
            }>
        }
    } = {}

    remoteTranslateLocaleFields(values:Array<string>, selectedLanguages:Array<string>) {
        return new Observable<Array<string>>((observer) => {
            let _translations = []

            for(let _item of values) {
                _translations.push(PGUtilities.tryParseJSON(_item) || {})
            }

            let _toTranslate = {}

            for(let i = 0; i < values.length; i++) {
                let _sourceLanguage:string = null;
                let _sourceTranslation:string = null;
                
                for(let _language of this.availableApplicationLanguages) {
                    if(_translations[i][_language] && selectedLanguages.indexOf(_language) != -1) {
                        _sourceLanguage = _language;
                        _sourceTranslation = _translations[i][_sourceLanguage]
                    }
                }
                
                if(_sourceTranslation) {
                    for(let _language of this.availableApplicationLanguages) {
                        if(selectedLanguages.indexOf(_language) == -1) {
                            if(_toTranslate[_sourceLanguage] == null) _toTranslate[_sourceLanguage] = {}
                            if(_toTranslate[_sourceLanguage][_language] == null) _toTranslate[_sourceLanguage][_language] = []

                            _toTranslate[_sourceLanguage][_language].push(i)
                        }
                    }
                }
            }

            let _reqNum = 0;

            for(let i in _toTranslate) {
                for(let j in _toTranslate[i]) {
                    let _requestList = []

                    for(let _index of _toTranslate[i][j]) {
                        _requestList.push(_translations[_index][i])
                    }

                    _reqNum++

                    this.remoteTranslateList(_requestList, i, j).subscribe((data) => {
                        for(let k = 0; k < data.length; k++) {
                            let _index = _toTranslate[i][j][k]
                            _translations[_index][j] = data[k]
                        }

                        _reqNum--
                        if(_reqNum == 0) {
                            let _retVal:Array<string> = []
                            for(let _item of _translations) {
                                _retVal.push(JSON.stringify(_item))
                            }

                            observer.next(_retVal)
                            observer.unsubscribe();
                        }
                    })
                }
            }

            if(_reqNum == 0) {
                setTimeout(() => {
                    let _retVal:Array<string> = []
                    for(let _item of _translations) {
                        _retVal.push(JSON.stringify(_item))
                    }

                    observer.next(_retVal)
                    observer.unsubscribe();
                }, 250)
            }
        })
    }

    remoteTranslate(text:string, source:string, target:string) {
        return this.remoteTranslateList([text], source, target).pipe(map((val) => {
            return val[0]
        }))
    }

    remoteTranslateList(text:Array<string>, source:string, target:string) {
        return new Observable<Array<string>>((observer) => {
            target = target.split('_')[0];
            
            if(source != null) {
                source = source.split('_')[0];
            }
            
            let _retVal:Array<string> = [];

            let _toTranslate:Array<number> = [];

            for(let i = 0; i < text.length; i++) {
                if(this._remoteTranslateCache[source] != null && this._remoteTranslateCache[source][target] != null) {
                    for(let _translation of this._remoteTranslateCache[source][target]) {
                        if(_translation.original == text[i]) {    
                            _retVal[i] = _translation.translated
                            break;
                        }
                    }
                }

                if(_retVal[i] == null) _toTranslate.push(i);
            }

            if(_toTranslate.length == 0) {
                setTimeout(() => {
                    observer.next(_retVal)
                    observer.unsubscribe()
                }, 100)
            }
            else {
                let _toSend:Array<string> = []
                for(let _index of _toTranslate) {
                    _toSend.push(text[_index])
                }

                this.http.post(this.environmentService.environment.APIUrl + '/translate', {
                    data: _toSend,
                    source_language: source,
                    target_language: target
                }).subscribe((data:any) => {
                    if(this._remoteTranslateCache[source] == null) this._remoteTranslateCache[source] = {}
                    if(this._remoteTranslateCache[source][target] == null) this._remoteTranslateCache[source][target] = []

                    for(let i = 0; i < _toTranslate.length; i++) {
                        _retVal[_toTranslate[i]] = data.data[i]

                        this._remoteTranslateCache[source][target].push({
                            original: text[_toTranslate[i]],
                            translated: data.data[i]
                        })
                    }

                    observer.next(_retVal)
                    observer.unsubscribe()
                }, (error) => {
                    observer.error(error)
                    observer.unsubscribe()
                })
            }
        })
    }

    private _isTranslationObject(value:any) {
        if(value != null && typeof value == 'object') {
            for(let i in value) {
                if(!/^[a-z][a-z]_[A-Z][A-Z]$/.test(i)) return false;
            }

            return true;
        }
        else return false;
    }

    getTranslatedValue(value:string|{ [lang:string]: string }, preserveNull?:boolean) {
        if(value == null) {
            return preserveNull ? null : '';
        }
        else {
            if(typeof value == 'string') {
                try {
                    let _parsed = JSON.parse(value);
                    if(this._isTranslationObject(_parsed)) value = _parsed;
                }
                catch(ex) {

                }
            }
            
            if(!this._isTranslationObject(value)) return value;
            else {
                if(value[this.currentLanguage] != null) {
                    return value[this.currentLanguage];
                }
                else {
                    return value['en_EN']
                }
            }
        }
    }

    private _addedIconAliases:{ [id:string] : string } = {}

    addIconAlias(id:string, aliasOf:string) {
        this._addedIconAliases[id] = aliasOf;
    }

    private _addedIconLinks:{ [id:string] : string } = {}

    addIconLink(id:string, aliasOf:string) {
        this._addedIconLinks[id] = aliasOf;
    }

    private _getIconOrObject(id:string):string|{ [id:string]:string } {
        if(this.icons == null) return null;
        else {
            for(let i in this._addedIconAliases) {
                if(id.startsWith(i)) {
                    id = id.replace(i, this._addedIconAliases[i])
                }
            }

            if(this._addedIconAliases[id] != null) id = this._addedIconAliases[id];
            else {
                for(let i in this._addedIconLinks) {
                    if(id.startsWith(i)) {
                        id = id.replace(i, this._addedIconLinks[i])
                    }
                }
            }

            let _retVal = null;

            let _idSplit = id.split('.');
            _retVal = this.icons;
    
            for(let i = 0; i < _idSplit.length; i++) {
                if(_retVal != null) _retVal = _retVal[_idSplit[i]];
            }
    
            return _retVal;
        }
    }

    icon(id:string, variant?:string) {
        if(variant == null) variant = 'fa'

        let _icon = this._getIconOrObject(id);

        if(_icon == null) return null;
        else if(typeof _icon == 'string') return variant + ' fa-' + _icon;
        else if(typeof _icon.pro == 'string') return variant + ' fa-' + _icon.pro;
        else return null;
    }

    getPrivacyPolicyString(realm?:string) {
        if(realm != null) {
            let _translation = 'REALMS.' + realm + '.privacy-policy';
            if(typeof this._getTranslationOrObject(_translation) == 'string') {
                return _translation
            }
        }
        
        return 'ENTOURANCE.privacy-policy'
    }

    getTermsAndConditionsString(realm?:string) {
        if(realm != null) {
            let _translation = 'REALMS.' + realm + '.terms-and-conditions';
            if(typeof this._getTranslationOrObject(_translation) == 'string') {
                return _translation
            }
        }

        return 'ENTOURANCE.terms-and-conditions'
    }

    getContentUpdatedAt(tid:string):string|null {
        return this.contentDataByTid?.[tid]?.updated_at ?? null;
    }

    getPrivacyPolicyUpdatedAt(realm?:string):string|null {
        return this.getContentUpdatedAt(this.getPrivacyPolicyString(realm));
    }

    getTermsAndConditionsUpdatedAt(realm?:string):string|null {
        return this.getContentUpdatedAt(this.getTermsAndConditionsString(realm));
    }

    getPrivacyPolicyURL(realm?:string) {
        if(this.environmentService.environment.PrivacyPolicyURL != null) return this.environmentService.environment.PrivacyPolicyURL;
        else return this.environmentService.getApplicationURL('Viewer') + '/content/' + this.getPrivacyPolicyString(realm)
    }

    getTermsAndConditionsURL(realm?:string) {
        if(this.environmentService.environment.TermsAndConditionsURL != null) return this.environmentService.environment.TermsAndConditionsURL;
        else return this.environmentService.getApplicationURL('Viewer') + '/content/' + this.getTermsAndConditionsString(realm)
    }

    disableTranslations = false;

    setDisableTranslations(value:boolean, skipRefresh?:boolean) {
        this.disableTranslations = value;

        this.persistenceService.setItem('POIGEST_DisableTranslations', this.disableTranslations ? 'true' : 'false', 'technical');

        if(!skipRefresh) {
            this.currentLanguageChange.next(this.currentLanguage)
        }
    }
}

@Injectable({
    providedIn: 'root',
})
export class LocalizationDateParserFormatter extends NgbDateParserFormatter {
    constructor(private localizationService:LocalizationService) {
        super();
    }

    parse(value: string): NgbDateStruct {
        if(value != null) {
            let _dateFormat = this.localizationService.data.dateFormats.short;
            if(_dateFormat == null) _dateFormat = 'dd/LL/yyyy';
            else {
                // Converti formato moment → luxon
                _dateFormat = _dateFormat.replace(/YYYY/g, 'yyyy').replace(/DD/g, 'dd').replace(/MM/g, 'LL');
            }

            let _cDate = DateTime.fromFormat(value, _dateFormat);

            if(_cDate.isValid) {
                return {
                    day: _cDate.day,
                    month: _cDate.month,
                    year: _cDate.year
                };
            }
        }

        return null;
    }

    format(date: NgbDateStruct): string {
        if(date != null) {
            let _dateFormat = this.localizationService.data.dateFormats.short;
            if(_dateFormat == null) _dateFormat = 'dd/LL/yyyy';
            else {
                // Converti formato moment → luxon
                _dateFormat = _dateFormat.replace(/YYYY/g, 'yyyy').replace(/DD/g, 'dd').replace(/MM/g, 'LL');
            }

            let _cDate = DateTime.fromJSDate(new Date(date.year, date.month - 1, date.day));

            if(_cDate.isValid) {
                return _cDate.toFormat(_dateFormat);
            }
        }

        return '';
    }
}

@Injectable({
    providedIn: 'root',
})
export class LocalizationDateAdapter extends NgbDateAdapter<string> {
    fromModel(value: string): NgbDateStruct {
        if(value != null) {
            let _cDate = DateTime.fromFormat(value, 'yyyy-LL-dd');

            if(_cDate.isValid) {
                return {
                    day: _cDate.day,
                    month: _cDate.month,
                    year: _cDate.year
                };
            }
        }

        return null;
    }

    toModel(date: NgbDateStruct): string {
        if(date != null) {
            let _cDate = DateTime.fromJSDate(new Date(date.year, date.month - 1, date.day));

            if(_cDate.isValid) {
                return _cDate.toFormat('yyyy-LL-dd');
            }
        }

        return null;
    };
}

@Injectable({
    providedIn: 'root',
})
export class LocalizationatepickerI18n extends NgbDatepickerI18n {
    constructor(private localizationService:LocalizationService) {
        super();
    }

    getDayAriaLabel(date:NgbDateStruct) { return this.localizationService.format.date(new Date(date.year, date.month - 1, date.day), true); }
    getDayNumerals(date:NgbDateStruct) { return date.day.toString(); }
    getMonthFullName(month:number) { return this.localizationService.data.monthNames[month - 1]; }
    getMonthShortName(month:number) { return this.localizationService.data.monthNames[month - 1].substr(0, 3); }
    getWeekdayLabel(weekNumber:number) { return this.localizationService.data.dayNames[weekNumber - 1]; }
    getWeekNumerals(weekNumber:number) { return weekNumber.toString(); }
    getWeekdayShortName(weekDay:number) { return this.localizationService.data.dayNames[weekDay % 7].substr(0, 3); }
    getYearNumerals(year:number) { return year.toString(); }
}